import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { del, get, post } from '@/shared/api/api-client';
import { usePermission } from '@/auth/permission-gate';
import {
  AMBIENT_RECORDING_DOWNLOAD_PERMISSION,
  recordingErrorMessage,
  type AmbientRecording,
  type AmbientRecordingDownload,
  type AmbientRecordingList,
  type AmbientRecordingsPort,
  type StartAmbientRecordingBody,
  type StartAmbientRecordingResult,
} from '../ambient/ambient-recordings-types';

// PROD-3181 — the Scrips.Patient ambient recording endpoints (PROD-2702). See
// `ambient-recordings-types.ts` for the routes and shapes. Errors are NOT caught here: they reach
// the panel, which says what happened in plain words.

const base = (patientId: string, encounterId: string) =>
  `/api/Patients/${encodeURIComponent(patientId)}/Encounters/${encodeURIComponent(encounterId)}/AmbientRecordings`;

/**
 * Download is hidden for now (Andrew, 10 Oct 2026): the panel offers only recording more than once
 * (New recording, Keep both / Replace) and Delete before the sign. Set true to bring the
 * permission-gated Download back; nothing else changes.
 */
export const AMBIENT_RECORDING_DOWNLOAD_ENABLED = false;

export const ambientRecordingsKey = (patientId?: string | null, encounterId?: string | null) =>
  ['ambient-recordings', patientId, encounterId] as const;

/** The port the ambient panel registers, lists, deletes and downloads recordings through. */
export function useAmbientRecordingsPort(
  patientId: string | null | undefined,
  encounterId: string | null | undefined,
): AmbientRecordingsPort | undefined {
  const queryClient = useQueryClient();
  const hasDownloadPermission = usePermission([AMBIENT_RECORDING_DOWNLOAD_PERMISSION]);
  const canDownload = AMBIENT_RECORDING_DOWNLOAD_ENABLED && hasDownloadPermission;
  const query = useQuery<AmbientRecordingList>({
    queryKey: ambientRecordingsKey(patientId, encounterId),
    enabled: !!patientId && !!encounterId,
    refetchOnWindowFocus: false,
    queryFn: async ({ signal }) =>
      await get<AmbientRecordingList>(base(patientId as string, encounterId as string), { signal }),
  });
  const data = query.data;
  const readError = query.error ? recordingErrorMessage(query.error) : null;
  const refetch = query.refetch;

  return useMemo(() => {
    if (!patientId || !encounterId) return undefined;
    const url = base(patientId, encounterId);
    const invalidate = () =>
      void queryClient.invalidateQueries({ queryKey: ambientRecordingsKey(patientId, encounterId) });
    return {
      list: data,
      readError,
      canDownload,
      refresh: async () => (await refetch()).data,
      start: async (body: StartAmbientRecordingBody) => {
        const result = await post<StartAmbientRecordingResult>(url, body);
        invalidate();
        return result;
      },
      remove: async (recordingId: string) => {
        const result = await del<AmbientRecording>(`${url}/${encodeURIComponent(recordingId)}`);
        invalidate();
        return result;
      },
      download: async (recordingId: string) =>
        await post<AmbientRecordingDownload>(`${url}/${encodeURIComponent(recordingId)}/Download`, {}),
    };
  }, [patientId, encounterId, data, readError, canDownload, refetch, queryClient]);
}
